import React from 'react';
import { HelpTooltip } from './HelpTooltip';

interface InvestmentModeProps {
  mode: 'auto' | 'custom';
  customAmount?: number;
  sponsorHours?: number;
  opsHours?: number;
  itHours?: number;
  trainingHours?: number;
  miscHours?: number;
  hourlyRates?: {
    sponsor: number;
    ops: number;
    it: number;
    training: number;
    misc: number;
  };
  onModeChange: (mode: 'auto' | 'custom') => void;
  onCustomAmountChange: (amount: number | undefined) => void;
  onHoursChange: (role: 'sponsor' | 'ops' | 'it' | 'training' | 'misc', hours: number) => void;
  onRateChange: (role: 'sponsor' | 'ops' | 'it' | 'training' | 'misc', rate: number) => void;
  disabled?: boolean;
  currency?: 'USD' | 'EUR';
}

export const InvestmentMode: React.FC<InvestmentModeProps> = ({
  mode,
  customAmount,
  sponsorHours = 0,
  opsHours = 0,
  itHours = 0,
  trainingHours = 0,
  miscHours = 0,
  hourlyRates = {
    sponsor: 150,
    ops: 100,
    it: 125,
    training: 75,
    misc: 100,
  },
  onModeChange,
  onCustomAmountChange,
  onHoursChange,
  onRateChange,
  disabled = false,
  currency = 'USD',
}) => {
  const currencySymbol = currency === 'EUR' ? '€' : '$';
  
  const calculateTotalFromRoles = () => {
    return (
      sponsorHours * hourlyRates.sponsor +
      opsHours * hourlyRates.ops +
      itHours * hourlyRates.it +
      trainingHours * hourlyRates.training +
      miscHours * hourlyRates.misc
    );
  };

  const roles = [
    { id: 'sponsor' as const, label: 'Executive Sponsor', defaultRate: 150 },
    { id: 'ops' as const, label: 'Operations Team', defaultRate: 100 },
    { id: 'it' as const, label: 'IT Support', defaultRate: 125 },
    { id: 'training' as const, label: 'Training & Enablement', defaultRate: 75 },
    { id: 'misc' as const, label: 'Other/Miscellaneous', defaultRate: 100 },
  ];

  const getRoleHours = (role: 'sponsor' | 'ops' | 'it' | 'training' | 'misc') => {
    switch (role) {
      case 'sponsor': return sponsorHours;
      case 'ops': return opsHours;
      case 'it': return itHours;
      case 'training': return trainingHours;
      case 'misc': return miscHours;
    }
  };

  return (
    <div className="bg-white p-5 rounded-lg border border-gray-200">
      <div className="flex items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Implementation Investment</h3>
        <HelpTooltip
          title="One-Time Implementation Cost"
          content="The internal resources and effort required to implement the scheduling platform. This includes project management, training, change management, and technical setup."
          details="Industry standard is 15-25% of annual subscription. Actual costs vary based on organization size, complexity, and implementation approach."
        />
      </div>
      
      {/* Mode Selection */}
      <div className="mb-4">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => onModeChange('auto')}
            disabled={disabled}
            className={`
              flex-1 py-2 px-4 rounded-lg border-2 transition-all duration-200
              ${mode === 'auto' 
                ? 'bg-indigo-50 border-indigo-500 text-indigo-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center gap-1">
              <span className="font-medium">Automatic</span>
              <HelpTooltip
                title="Automatic Calculation"
                content="Calculates implementation cost based on industry benchmarks. Scales with implementation timeline duration (longer implementations = more internal effort required)."
                details="Formula: (20 hours/week × implementation weeks × $150/hour) + 10% of annual subscription for vendor support."
                position="bottom"
              />
            </div>
            <div className="text-xs mt-1">Industry standard (scales with timeline)</div>
          </button>
          
          <button
            type="button"
            onClick={() => onModeChange('custom')}
            disabled={disabled}
            className={`
              flex-1 py-2 px-4 rounded-lg border-2 transition-all duration-200
              ${mode === 'custom' 
                ? 'bg-indigo-50 border-indigo-500 text-indigo-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center gap-1">
              <span className="font-medium">Custom</span>
              <HelpTooltip
                title="Custom Calculation"
                content="Manually specify implementation costs based on your organization's specific needs. Use this when you have detailed resource planning or vendor quotes."
                details="You can enter a total amount or break down hours by role. Ideal for organizations with established project estimates."
                position="bottom"
              />
            </div>
            <div className="text-xs mt-1">Specify your own investment</div>
          </button>
        </div>
      </div>

      {/* Auto Mode Info */}
      {mode === 'auto' && (
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Automatic Calculation</p>
              <p className="text-xs">
                Implementation investment is calculated as 20% of the annual platform subscription, 
                representing typical internal effort for rollout, training, and change management.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Custom Mode Inputs */}
      {mode === 'custom' && (
        <div className="space-y-4">
          {/* Lump Sum Option */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Option 1: Enter Total Investment
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                {currencySymbol}
              </span>
              <input
                type="number"
                value={customAmount || ''}
                onChange={(e) => onCustomAmountChange(e.target.value ? Number(e.target.value) : undefined)}
                disabled={disabled}
                placeholder="0"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">OR</span>
            </div>
          </div>

          {/* Role-Based Calculation */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-3">
              <label className="text-sm font-medium text-gray-700">
                Option 2: Calculate from Role Allocations
              </label>
              <HelpTooltip
                title="Resource Breakdown"
                content="Estimate implementation effort by role. This helps identify resource requirements and budget allocation across different teams."
                details="Typical hours: Executive Sponsor (40-80), Operations (100-200), IT (80-160), Training (60-120). Adjust based on your organization's complexity."
              />
            </div>
            
            <div className="space-y-3">
              {roles.map((role) => (
                <div key={role.id} className="grid grid-cols-3 gap-2 items-center">
                  <label className="text-xs text-gray-600">{role.label}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={getRoleHours(role.id) || ''}
                      onChange={(e) => onHoursChange(role.id, Number(e.target.value) || 0)}
                      disabled={disabled}
                      placeholder="0"
                      className="w-full pr-8 pl-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      hrs
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      value={hourlyRates[role.id]}
                      onChange={(e) => onRateChange(role.id, Number(e.target.value) || 0)}
                      disabled={disabled}
                      className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      /hr
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Total Calculation */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Total Investment:</span>
                <span className="text-lg font-bold text-indigo-600">
                  {currencySymbol}{calculateTotalFromRoles().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Investment Note */}
      <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-start">
          <svg className="w-4 h-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p className="text-xs text-yellow-800">
            <span className="font-medium">Note:</span> This represents one-time internal effort costs only. 
            Platform subscription is calculated separately and included in ROI calculations.
          </p>
        </div>
      </div>
    </div>
  );
};